import { Box, ButtonBase } from '@mui/material';
import { EditOutlined } from '@mui/icons-material';
import { ValueText } from '~/components/Fields/LabeledValue';
import { SIDE_PANEL_ATTRIBUTE } from '~/components/Properties/PropertiesSidePanel';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { useStructureColorOpener } from '~/hooks/structure-set/useStructureColorEdit';
import { dicomColorToHex } from '~/utils/colorUtils';
import RoiColor from './RoiColor';

const VALUE_SX = { display: 'inline-flex', alignItems: 'center', gap: 0.5 };

/**
 * A structure's colour as a value: a swatch, its hex and its R,G,B.
 * Follows an open colour edit live. Unless the structure's colour edit is
 * withheld, the whole value is the button that opens it beside the panel
 * the value sits in.
 */
export default function StructureColorValue({ roi }: { roi: RoiReadModel }) {
    const { color, withheldReasonId, toggle, openerProps } =
        useStructureColorOpener(roi, 'properties');
    const content = (
        <>
            <RoiColor color={color} size={12} />
            <Box component="span" fontFamily="fontFamilyMonospace">
                {dicomColorToHex(color)}
            </Box>
            <Box component="span" color="text.secondary" fontWeight={400}>
                {`${color.R}, ${color.G}, ${color.B}`}
            </Box>
        </>
    );

    return withheldReasonId === undefined ? (
        <ButtonBase
            disableRipple
            {...openerProps}
            data-testid="structure-properties-color-value"
            onClick={({ currentTarget }) =>
                // Beside the Properties panel, not on top of it.
                toggle(
                    currentTarget.closest<HTMLElement>(
                        `[${SIDE_PANEL_ATTRIBUTE}]`
                    ) ?? currentTarget
                )
            }
            sx={{
                ...VALUE_SX,
                justifySelf: 'start',
                m: '-2px -4px',
                p: '2px 4px',
                borderRadius: 0.5,
                font: 'inherit',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                '&:hover, &:focus-visible, &[aria-expanded="true"]': {
                    bgcolor: 'action.hover',
                },
                '&:focus-visible': { outline: 1, outlineColor: 'primary.main' },
            }}
        >
            {content}
            <EditOutlined
                aria-hidden
                sx={{ fontSize: 14, color: 'primary.main' }}
            />
        </ButtonBase>
    ) : (
        <ValueText
            component="span"
            data-testid="structure-properties-color-value"
            sx={VALUE_SX}
        >
            {content}
        </ValueText>
    );
}
